import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';

import { Page } from '@nearst/ui';

// components
import AnalyticsWrapper from '@components/AnalyticsWrapper';
import ErrorBoundary from '@components/ErrorBoundary';
import LoadingContainer from '@components/LoadingContainer';
import MyShopNavigation from '@components/MyShopNavigation';
import PrivateRoute from '@components/PrivateRouteComponent';
import Sidebar from '@components/Sidebar';
import { AssistantSidebar } from '@components/AssistantSidebar';
import { MobileAssistantFab } from '@components/AssistantSidebar/MobileAssistantFab';

// services
import { ShopProvider, useShop } from '@services/ShopProvider';
import { UpgradeModalProvider } from '@services/UpgradeProvider';
import { InsightsDateProvider } from '@services/InsightsDateProvider';
import { AssistantProvider } from '@services/AssistantProvider';
import ScrollToTop from '@utils/ScrollToTop';
import UpdatePopup from '@components/UpdatePopup';
import styles from './rootLayout.module.scss';

// Inner component to access ShopProvider context
const AssistantProviderWrapper = ({ children, showAssistant }) => {
	const { shop } = useShop();
	const { user } = useUser();

	if (!showAssistant) return children;

	return (
		<AssistantProvider mode="general" currentShop={shop} userId={user?.id}>
			{children}
		</AssistantProvider>
	);
};

export const RootLayout = () => {
	const location = useLocation();
	const { user } = useUser();
	const showAssistant = user?.unsafeMetadata?.product === 'unify';

	return (
		<PrivateRoute>
			<ShopProvider>
				<AssistantProviderWrapper showAssistant={showAssistant}>
					<AnalyticsWrapper>
						<InsightsDateProvider>
							<UpgradeModalProvider>
								<ScrollToTop />
								<UpdatePopup />
								<Sidebar.Layout>
									<MyShopNavigation />
									{showAssistant && <AssistantSidebar />}
									<Page.Main fallback={<LoadingContainer />} className={showAssistant && styles.assistantVisible}>
										<ErrorBoundary>
											<Outlet key={location.pathname} />
										</ErrorBoundary>
									</Page.Main>
								</Sidebar.Layout>
								{showAssistant && <MobileAssistantFab />}
							</UpgradeModalProvider>
						</InsightsDateProvider>
					</AnalyticsWrapper>
				</AssistantProviderWrapper>
			</ShopProvider>
		</PrivateRoute>
	);
};
